"use client";

import type { FC } from "react";
import { useState } from "react";
import { Card, Checkbox } from "@akashnetwork/ui/components";
import Link from "next/link";
import { DeploymentProjectChip } from "@src/components/projects/DeploymentProjectChip/DeploymentProjectChip";
import { useDeploymentStatus } from "@src/hooks/useDeploymentStatus/useDeploymentStatus";
import type { ListedDeploymentDto } from "@src/types/deployment";
import type { ApiProviderList } from "@src/types/provider";
import { UrlService } from "@src/utils/urlUtils";
import { resolveDeploymentGpus } from "../DeploymentDetail/DeploymentPlacements/placementModel";
import { DeploymentStatusBadge } from "../DeploymentDetail/DeploymentStatusBadge";
import { DeploymentRecoveryAction } from "../DeploymentRecoveryAction/DeploymentRecoveryAction";
import { DeploymentActionsMenu } from "./DeploymentActionsMenu";
import { DeploymentBadges } from "./DeploymentBadges";
import { DeploymentEndpoints, DeploymentEndpointsPanel } from "./DeploymentEndpoints";
import { DeploymentSpecSummary } from "./DeploymentSpecSummary";
import { useDeploymentReachability } from "./useDeploymentReachability";
export const DEPENDENCIES = {
  useDeploymentReachability,
  useDeploymentStatus,
  DeploymentStatusBadge,
  DeploymentRecoveryAction,
  DeploymentBadges,
  DeploymentEndpoints,
  DeploymentEndpointsPanel,
  DeploymentSpecSummary,
  DeploymentActionsMenu,
  DeploymentProjectChip
};
export interface DeploymentCardProps {
  deployment: ListedDeploymentDto;
  providers: ApiProviderList[] | undefined;
  isSelectable?: boolean;
  isSelected?: boolean;
  isClosing?: boolean;
  canManage?: boolean;
  onSelect?: (input: {
    id: string;
    isShiftPressed: boolean;
  }) => void;
  onDeploymentClosed?: () => void;
  dependencies?: typeof DEPENDENCIES;
}
export const DeploymentCard: FC<DeploymentCardProps> = ({
  deployment,
  providers,
  isSelectable,
  isSelected,
  isClosing = false,
  canManage = true,
  onSelect,
  onDeploymentClosed,
  dependencies: d = DEPENDENCIES
}) => {
  const {
    leases,
    workload,
    endpoints,
    isLoadingEndpoints,
    unreachableReason
  } = d.useDeploymentReachability({
    deployment,
    providers
  });
  const status = d.useDeploymentStatus({
    dseq: deployment.dseq,
    state: deployment.state,
    leases,
    workload
  });
  const [isShowingEndpoints, setIsShowingEndpoints] = useState(false);
  const toggleEndpoints = () => setIsShowingEndpoints(current => !current);
  /** Endpoints can drop below the count that draws the toggle while the panel is open, which would strand it with nothing left to close it. */
  const isShowingEndpointPanel = isShowingEndpoints && endpoints.length > 1;
  return <Card className="flex flex-col overflow-hidden transition-[box-shadow,border-color] duration-200 hover:border-zinc-400 hover:shadow-md dark:hover:border-zinc-600" data-sentry-element="Card" data-sentry-component="DeploymentCard" data-sentry-source-file="DeploymentCard.tsx">
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <Link href={UrlService.deploymentDetails(deployment.dseq)} className="min-w-0 truncate font-semibold hover:underline" data-sentry-element="Link" data-sentry-source-file="DeploymentCard.tsx">
              {deployment.name || `Deployment #${deployment.dseq}`}
            </Link>
            <d.DeploymentProjectChip projectId={deployment.projectId} data-sentry-element="d.DeploymentProjectChip" data-sentry-source-file="DeploymentCard.tsx" />
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            <d.DeploymentStatusBadge status={status} leases={leases} isSummarized data-sentry-element="d.DeploymentStatusBadge" data-sentry-source-file="DeploymentCard.tsx" />
            <d.DeploymentBadges deployment={deployment} data-sentry-element="d.DeploymentBadges" data-sentry-source-file="DeploymentCard.tsx" />
          </div>
        </div>

        {status.launch?.phase === "failed" && canManage ? <d.DeploymentRecoveryAction dseq={deployment.dseq} recovery={status.launch.recovery} leases={leases ?? []} isCompact /> : <d.DeploymentEndpoints endpoints={endpoints} isLoading={isLoadingEndpoints} unreachableReason={unreachableReason} isSpinningUp={status.isSpinningUp} isExpanded={isShowingEndpointPanel} onToggleExpanded={toggleEndpoints} />}

        {isShowingEndpointPanel && <d.DeploymentEndpointsPanel endpoints={endpoints} />}
      </div>

      <div className="flex items-center justify-between gap-3 border-t px-5 py-2">
        <d.DeploymentSpecSummary deployment={deployment} resolvedGpus={resolveDeploymentGpus(deployment.leases)} className="min-w-0 flex-1" data-sentry-element="d.DeploymentSpecSummary" data-sentry-source-file="DeploymentCard.tsx" />

        <div className="flex shrink-0 items-center gap-1">
          {isSelectable && !isClosing && <Checkbox aria-label={`Select deployment ${deployment.name || deployment.dseq}`} checked={isSelected} expandedTouchTarget className="shrink-0" onClick={event => onSelect?.({
          id: deployment.dseq,
          isShiftPressed: event.shiftKey
        })} />}
          <d.DeploymentActionsMenu deployment={deployment} onDeploymentClosed={onDeploymentClosed} isClosing={isClosing} canManage={canManage} data-sentry-element="d.DeploymentActionsMenu" data-sentry-source-file="DeploymentCard.tsx" />
        </div>
      </div>
    </Card>;
};